<template>
  <div class="lib__form__main">
    <div class="lib__form__main__content">
      <div class="swde-info-modal">
        <ModalDialog :closeable="$t('common.close')" :open.sync="isSwdeInfoModalOpen">
          <p v-html="$t('new-connection-cmh.offering-selection-step.swde-info')"></p>
        </ModalDialog>
      </div>
      <ValidationObserver slim ref="validator">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('resume.title') }}</h2>
          <h3 class="lib__h3">{{ $t('new-connection-cmh.summary-step.subTitle') }}</h3>
        </header>
        <resume-section-title minimumBadgeWidth :read-only="readOnly" :step="1" @goToStep="goToStep($event)">
          <template v-slot:icon> 1 </template>
          <template>{{ $t('resume.address') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-address-cmh-mobile :address="stateAddressPoint.addressPoint"
              :buildingType="stateAddressPoint.selection" />
          </template>
        </resume-container>
        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="2"
          @goToStep="goToStep($event)">
          <template v-slot:icon> 2 </template>
          <template>{{ $t('resume.request') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div v-if="isHouseSelected && statePackagedElectricityMeters && statePackagedElectricityMeters.length">
              <resume-offering-header :header="$t('new-connection-cmh.offering-selection-step.electricity')">
                <img class="lib__header__grid--img"
                  :src="require('@ores/css-library/assets/img/icons/electricity.svg')" />
              </resume-offering-header>
              <div v-for="(item, n) in statePackagedElectricityMeters" :key="`${n}-electricity`">
                <package-meter-summarized :meter="item" :isPriceToBeDetermined="item.customPower"
                  :applicableVAT="applicableVAT" :buildingType="'house'"></package-meter-summarized>
                <price-disclaimer v-if="displayPriceDisclaimer((item.package && item.package.homePrice) || null)"></price-disclaimer>
              </div>
              <div v-for="(item, n) in stateModifiedConnections" :key="`${n}-modified-connection`">
                <add-connection-summarized :meter="item" :applicableVAT="applicableVAT"></add-connection-summarized>
              </div>
            </div>
            <div v-if="isBuildingSelected && statePackagedElectricityMeters && statePackagedElectricityMeters.length">
              <resume-offering-header :header="$t('new-connection-cmh.offering-selection-step.electricity')">
                <img class="lib__header__grid--img"
                  :src="require('@ores/css-library/assets/img/icons/electricity.svg')" />
              </resume-offering-header>
              <div v-for="(item, n) in statePackagedElectricityMeters" :key="`${n}-flat-electricity`">
                <template v-if="item.destination === 'residential'">
                  <package-meter-summarized :meter="item" :isPriceToBeDetermined="true" :applicableVAT="applicableVAT"
                    :buildingType="'building'"></package-meter-summarized>
                  <price-disclaimer
                    v-if="displayPriceDisclaimer((item.package && item.package.buildingPrice) || null)"></price-disclaimer>
                </template>
              </div>
              <div v-for="(item, n) in statePackagedElectricityMeters" :key="`${n}-common-electricity`">
                <template v-if="item.destination === 'common'">
                  <package-meter-summarized v-if="item.destination === 'common'" :meter="item"
                    :isPriceToBeDetermined="true" :applicableVAT="applicableVAT"
                    :buildingType="'building'"></package-meter-summarized>
                  <price-disclaimer
                    v-if="displayPriceDisclaimer((item.package && item.package.buildingPrice) || null)"></price-disclaimer>
                </template>
              </div>
            </div>
            <div v-if="isGasValid">
              <resume-offering-header :header="$t('new-connection-cmh.offering-selection-step.gas')">
                <img class="lib__header__grid--img" :src="gasImageUrl" />
              </resume-offering-header>
              <div v-for="(item, n) in stateGasMeters" :key="`${n}-gas`">
                <meter-editable-box readonly :title="getGasMeterTitle(item)" :description="getGasMeterDescription(item)"
                  :identifier="n" :onEdit="editBox" :onDelete="editBox" :isPriceToBeDetermined="false" />
              </div>
            </div>
            <template v-if="isCmhAvailable">
              <div v-if="isSwdeSelected">
                <resume-offering-header :header="$t('new-connection-cmh.offering-selection-step.water')">
                  <img class="lib__header__grid--img" :src="require('@/assets/swde-logo.png')" alt="swde" />
                </resume-offering-header>
                <meter-editable-box readonly :title="$t('new-connection-cmh.summary-step.installationConnectionSwde')"
                  :description="''" :excludingVatPrice="formatNumberToLocaleStringFormat(10)" :identifier="1"
                  :onEdit="editBox" :onDelete="editBox" :isPriceToBeDetermined="true" />
              </div>
              <div v-if="isProximusSelected">
                <resume-offering-header :header="'Proximus'">
                  <img class="lib__header__grid--img" :src="require('@/assets/proximus-logo.png')" alt="proximus" />
                </resume-offering-header>
                <meter-editable-box readonly :title="$t('new-connection-cmh.summary-step.installationVooOrProximus')"
                  :description="''" :identifier="1" :onEdit="editBox" :onDelete="editBox"
                  :isPriceToBeDetermined="false" />
              </div>
              <div v-if="isOrangeSelected">
                <resume-offering-header header="Orange">
                  <img class="lib__header__grid--img orange-img" :src="require('@/assets/Orange.svg')" alt="orange" />
                </resume-offering-header>
                <meter-editable-box readonly :title="$t('new-connection-cmh.summary-step.installationVooOrProximus')"
                  :description="''" :identifier="1" :onEdit="editBox" :onDelete="editBox"
                  :isPriceToBeDetermined="false" />
              </div>
            </template>
            <div class="row">
              <div class="lib__text-weight-600 col-xs-6 col-sm-6 col-md-6 col-lg-6">
                {{ $t('new-connection-cmh.summary-step.indicativeTotalPrice') }}
              </div>
              <div class="lib__text-right col-xs-6 col-sm-6 col-md-6 col-lg-6">
                <span class="lib__text-ellipsis"> {{ fullPrice }} </span> <br />
                <span class="lib__text-info lib__text-small">{{ $t('meter.includingVATPrice') }}</span>
              </div>
            </div>
            <div class="row">
              <feedback v-if="!isOresConnection && !isCmhAvailable" icon simple level="alert">
                <h3>{{ $t('new-connection-cmh.summary-step.cmh-unavailable') }}</h3>
              </feedback>
            </div>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="4"
          @goToStep="goToStep($event)">
          <template v-slot:icon> 3 </template>
          <template>{{ $t('resume.energyProduction') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-energy-production :kVA="cmhEnergyProductionConfiguration.kvaPower || 0"
              :kWC="cmhEnergyProductionConfiguration.kwcPower || 0" :yesNoMaybeSelection="stateYesNoMaybeSelection" />
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="5"
          @goToStep="goToStep($event)">
          <template v-slot:icon> 4 </template>
          <template>{{ $t('resume.contact') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-contact-info isMobileLayoutApplied :address="stateAddressPoint.addressPoint"
              :contact-info="stateContactInfo"></resume-contact-info>
          </template>
        </resume-container>
        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="6"
          @goToStep="goToStep($event)">
          <template v-slot:icon> 5 </template>
          <template>{{ $t('breadcrumb.vat') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout borderless>
          <template v-slot:common>
            <resume-vat-info isMobileLayoutApplied :company-vat-info-selection="stateCompanyVatInfoSelection"
              :contact-info="stateContactInfo" :freelance-vat-info-selection="stateFreelanceVatInfoSelection"
              :individual-vat-info-selection="stateIndividualVatInfoSelection"></resume-vat-info>
            <br />
            <resume-files-cmh :fileInfo="stateFileInfo" mobileLayoutApplied />
          </template>
        </resume-container>
        <br />
        <p class="summary-conditions lib__text-info lib__text-center"
          v-html="$t('new-connection-cmh.energy-configuration-step.moreInfoOnOresConnectionPrices')"></p>
        <p class="summary-conditions lib__text-info lib__text-center"
          v-html="$t('new-connection-cmh.energy-configuration-step.seeConditionsHere')"></p>
        <p v-if="isSwdeSelected" class="summary-conditions lib__text-info lib__text-center">
          {{ $t('new-connection-cmh.summary-step.swdePriceForStandartConnection') }}
          <span @click="openSwdeInfoModal()"><i class="lib__text-small icon-swde-info fa fa-info-circle"></i> </span>
        </p>

        <Recaptcha :site-key='siteKey' :by-pass="$testMode" v-if="!readOnly" v-model="recaptchaToken" />
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator'
import Step from '@/components/steps/Step'
import MeterEditableBox from '@/components/inputs/Energy/MeterEditableBox.vue'
import {
  GasMeter,
  ContactInfo,
  IndividualVatInfoSelection,
  FreelanceVatInfoSelection,
  CompanyVatInfoSelection,
  PackagedElectricityMeter,
  ModifiedConnection,
  PriceDTO
} from '@/models'
import { State, Getter, Mutation } from 'vuex-class'
import { CmhAddressPoint } from '@/models/cmh-address-point'
import { YesNoMaybeSelection, FileInfo } from '@ores/vue-library'
import { CmhEnergyProductionConfigurationState } from '@/store/new-connection-cmh/cmh-energy-production-configuration/types'
import { pixelFacebookService } from '@ores/vue-library'
import ResumeAddress from '@/components/display/Resume/ResumeAddress.vue'
import GasPriceCalculator from '@/components/display/GasPriceCalculator.vue'
import ElectricityPriceCalculator from '@/components/display/ElectricityPriceCalculator.vue'
import ResumeContactInfo from '@/components/display/Resume/ResumeContactInfo.vue'
import ResumeVatInfo from '@/components/display/Resume/ResumeVatInfo.vue'
import ResumeOfferingHeader from '@/components/display/Resume/ResumeOfferingHeader.vue'
import ResumeAddressCmhMobile from '@/components/display/Resume/ResumeAddressCmhMobile.vue'
import ResumeFilesCmh from '@/components/display/Resume/ResumeFilesCmh.vue'
import ResumeEnergyProduction from '@/components/display/Resume/ResumeEnergyProduction.vue'
import { pricingService } from '@/services/pricing-service'
import PackageEditableBox from '@/components/display/CmhNewConnection/PackageEditableBox.vue'
import PackageMeterSummarized from '@/components/display/CmhNewConnection/PackageMeterSummarized.vue'
import { vatService } from '@/services'
import AddConnectionSummarized from '@/components/display/CmhNewConnection/AddConnectionSummarized.vue'
import { BuildingType } from '@/models/building-type'
import PriceDisclaimer from '@/components/display/CmhNewConnection/PriceDisclaimer.vue'

@Component({
  components: {
    ResumeAddress,
    GasPriceCalculator,
    ElectricityPriceCalculator,
    ResumeContactInfo,
    ResumeVatInfo,
    MeterEditableBox,
    PackageEditableBox,
    PackageMeterSummarized,
    PriceDisclaimer,
    AddConnectionSummarized,
    ResumeAddressCmhMobile,
    ResumeFilesCmh,
    ResumeOfferingHeader,
    ResumeEnergyProduction
  },
  name: 'cmh-summary-step'
})
export default class CmhSummaryStep extends Step {
  //#region [Data]
  @Prop({ type: Boolean, required: false, default: false }) public readonly readOnly!: boolean

  @State('requestId', { namespace: 'newConnectionCmh' })
  public requestId!: string

  @State('gasMeters', { namespace: 'newConnectionCmh/cmhEnergyConfiguration' })
  public stateGasMeters!: GasMeter[]
  @State('packagedElectricityMeters', { namespace: 'newConnectionCmh/cmhEnergyConfiguration' })
  public statePackagedElectricityMeters!: PackagedElectricityMeter[]
  @State('modifiedConnections', { namespace: 'newConnectionCmh/cmhEnergyConfiguration' })
  public stateModifiedConnections!: ModifiedConnection[]
  @State('addressPoint', { namespace: 'newConnectionCmh/cmhAddress' })
  public stateAddressPoint!: CmhAddressPoint
  @State('yesNoMaybeSelection', { namespace: 'newConnectionCmh/cmhEnergyProductionConfiguration' })
  public stateYesNoMaybeSelection!: YesNoMaybeSelection | null
  @State('cmhEnergyProductionConfiguration', { namespace: 'newConnectionCmh' })
  public cmhEnergyProductionConfiguration!: CmhEnergyProductionConfigurationState
  @State('contactInfo', { namespace: 'newConnectionCmh/cmhContact' })
  public stateContactInfo!: ContactInfo
  @State('individualVatInfoSelection', { namespace: 'newConnectionCmh/cmhVatInfo' })
  public stateIndividualVatInfoSelection!: IndividualVatInfoSelection | null
  @State('freelanceVatInfoSelection', { namespace: 'newConnectionCmh/cmhVatInfo' })
  public stateFreelanceVatInfoSelection!: FreelanceVatInfoSelection | null
  @State('companyVatInfoSelection', { namespace: 'newConnectionCmh/cmhVatInfo' })
  public stateCompanyVatInfoSelection!: CompanyVatInfoSelection | null
  @State('fileInfo', { namespace: 'newConnectionCmh/cmhFiles' })
  public stateFileInfo!: FileInfo[]

  @Getter('isOresConnection', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isOresConnection!: boolean
  @Getter('isElectricitySelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isElectricitySelected!: boolean
  @Getter('isGasSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isGasSelected!: boolean
  @Getter('isGasOresSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isGasOresSelected!: boolean
  @Getter('isGasResaSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isGasResaSelected!: boolean
  @Getter('isSwdeSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isSwdeSelected!: boolean
  @Getter('isProximusSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isProximusSelected!: boolean
  @Getter('isOrangeSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  public isOrangeSelected!: boolean
  @Getter('isProximusAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isProximusAvailable!: boolean
  @Getter('isOrangeAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isOrangeAvailable!: boolean
  @Getter('isSwdeAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isSwdeAvailable!: boolean
  @Getter('hasDocumentsAttached', { namespace: 'newConnectionCmh/cmhFiles' })
  public hasDocumentsAttached!: boolean
  @Getter('isCmhAvailable', { namespace: 'newConnectionCmh/cmhEnergyConfiguration' })
  public isCmhAvailable!: boolean
  @Getter('isGasManagerResa', { namespace: 'newConnectionCmh/cmhAddress' })
  private isGasManagerResa!: boolean
  @Mutation('STORE_TOTAL_PRICE', { namespace: 'newConnectionCmh' })
  public storeTotalPrice!: (totalPrice: number | null) => void

  public recaptchaToken: string | null = null
  public isSwdeInfoModalOpen: boolean = false

  public applicableVAT: string = ''

  public isHouseSelected: boolean = false
  public isBuildingSelected: boolean = false

  //#endregion

  //#region [Computed]
  public get siteKey() {
    return process.env.VUE_APP_CAPTCHA_SITE_KEY
  }
  public get hasTooManyMetersForCmh(): boolean {
    return !this.isCmhAvailable
  }

  public get gasImageUrl() {
    return require(this.isGasManagerResa ? '@/assets/Resa.svg' : '@ores/css-library/assets/img/icons/gas.svg')
  }

  public get isGasValid(): boolean {
    return this.stateGasMeters && this.stateGasMeters.length > 0 && (this.isGasManagerResa && this.isCmhAvailable || !this.isGasManagerResa)
  }
  //#endregion

  //#region [Method]
  public mounted() {
    this.isHouseSelected = this.stateAddressPoint.selection === BuildingType.house
    this.isBuildingSelected = this.stateAddressPoint.selection === BuildingType.building
    pixelFacebookService.trackViewContent({
      content_ids: ['CmhForm'],
      content_name: 'Summary Step',
      content_type: 'product'
    })
    this.getVATInfo()
  }

  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  public getVATInfo() {
    this.applicableVAT = vatService.getVATInfo(this.stateIndividualVatInfoSelection!, this.stateFreelanceVatInfoSelection!, this.stateCompanyVatInfoSelection!)
  }

  getVATLabel(): string {
    return vatService.getVatLabel(this.applicableVAT!)
  }

  public getGasMeterTitle(item: GasMeter) {
    return `${item.count}x ${this.$t('new-connection-cmh.energy-configuration-step.meter')} ${this.$t(
      'new-connection-cmh.energy-configuration-step.naturalGas'
    )}`
  }

  public getGasMeterDescription(item: GasMeter) {
    return `${this.$t('new-connection-cmh.energy-configuration-step.kitchenHeaterHotWater')} - ${this.$t(
      'new-connection-cmh.energy-configuration-step.max'
    )} ${item.power}kW`
  }

  public editBox(identifier: number) {
    /* place holder */
  }

  public formatNumberToLocaleStringFormat(str: number): string {
    return str.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }

  public getPriceIsToBeDetermined(): boolean {
    return pricingService.getPriceIsToBeDetermined(this.statePackagedElectricityMeters)
  }

  public get fullPrice() {
    if (this.getPriceIsToBeDetermined() || this.isBuildingSelected) {
      this.storeTotalPrice(null)
      return `${this.$t('new-connection-cmh.offering-selection-step.priceToBeEstimated')}`
    }
    let total = 0
    if (this.statePackagedElectricityMeters.length) {
      total += pricingService.getTotalPackagedElectricityMetersPrice(this.statePackagedElectricityMeters, this.applicableVAT, this.stateAddressPoint.selection)
    }
    if (this.stateModifiedConnections.length) {
      total += pricingService.getTotalModifiedConnectionsPrice(this.stateModifiedConnections, this.applicableVAT)
    }
    this.storeTotalPrice(total)
    return `${pricingService.formatNumberToLocaleStringFormat(total)} €`
  }

  public displayPriceDisclaimer(price: PriceDTO | null): boolean {
    if (price) {
      return price.exVat > 0
    }
    return false
  }

  public async save(next: boolean, valid: boolean): Promise<boolean> {
    if (!next || !valid) {
      return true
    }

    let files: string[] = []
    if (this.stateFileInfo && this.stateFileInfo.length > 0) {
      files = [...files, ...this.stateFileInfo.map((file: FileInfo) => file.name)]
    }

    if (this.stateAddressPoint && this.stateAddressPoint?.addressPoint?.subCity && this.stateContactInfo) {
      const me = this
      function getStepOperatorSelection() {
        const withStepOperatorSelection =
          me.stateAddressPoint!.addressPoint!.subCity.connectMyHomeActivated &&
          me.stateAddressPoint.addressPoint!.subCity.connectMyHomeInformation &&
          me.isCmhAvailable

        if (!withStepOperatorSelection) {
          return {
            operators: {
              proximus: null,
              orange: null,
              swde: null
            }
          }
        }

        return {
          operators: {
            proximus: me.isProximusAvailable ? me.isProximusSelected : null,
            orange: me.isOrangeAvailable ? me.isOrangeSelected : null,
            swde: me.isSwdeAvailable ? me.isSwdeSelected : null
          }
        }
      }

      const data = {
        requestId: this.requestId,
        stepAddress: {
          zipCode: this.stateAddressPoint.addressPoint.subCity.zipCode,
          subCityId: this.stateAddressPoint.addressPoint.subCity.georesSubcityId,
          streetSubCityId: this.stateAddressPoint.addressPoint.streetSubCity
            ? this.stateAddressPoint.addressPoint.streetSubCity.georesStreetSubcityId
            : null,
          houseNumber: this.stateAddressPoint.addressPoint.houseNumber,
          isMapLocation: this.stateAddressPoint.addressPoint.isMapLocation,
          mapLocation: this.stateAddressPoint.addressPoint.mapLocation,
          isNonFinalHouseNumber: this.stateAddressPoint.addressPoint.isNonFinalHouseNumber,
          buildingType: this.stateAddressPoint.selection,
          flatNumber: this.stateAddressPoint.flatNumber ? +this.stateAddressPoint.flatNumber : null,
        },
        stepAttachments: {
          attachDocuments: files.length > 0,
          files
        },
        stepConfigurationInfo: {
          type: 2,
          packagedElectricityMeters: this.statePackagedElectricityMeters,
          modifiedConnections: this.stateModifiedConnections,
          gasMeters: this.isGasValid ? this.stateGasMeters : []
        },
        stepContactInfo: this.stateContactInfo,
        stepEnergySelection: {
          electricity: this.isElectricitySelected,
          gas: this.isGasOresSelected && this.isGasValid,
          resa: this.isGasResaSelected && this.isGasValid
        },
        stepEnergyProductionInfo: {
          hasEnergyProduction: this.cmhEnergyProductionConfiguration.yesNoMaybeSelection,
          kVAPower: this.cmhEnergyProductionConfiguration.kvaPower,
          kwcPower: this.cmhEnergyProductionConfiguration.kwcPower
        },
        stepOperatorSelection: getStepOperatorSelection(),
        stepServicePlus: { servicePlusSelection: null },
        stepVatInfoSelection: {
          individualVatInfoSelection: this.stateIndividualVatInfoSelection,
          freelanceVatInfoSelection: this.stateFreelanceVatInfoSelection,
          companyVatInfoSelection: this.stateCompanyVatInfoSelection
        }
      }

      if (!this.recaptchaToken) {
        throw new Error('No recaptcha token found')
      }
      
      const response = await this.$api.post('api/worksrequest/new-connection-request', data, {
        headers: {
          'g-recaptcha-response': this.recaptchaToken
        }
      })

      if (response.status >= 200 && response.status < 300) {
        await this.$api.delete(`NewConnectionRequest/${this.requestId}`)
        const trackData: Record<string, string> = {
          Segment: 'WR_NCCMH',
          WebRef: this.requestId,
          postCode: this.stateAddressPoint.addressPoint.subCity.zipCode,
          withCmh: this.stateAddressPoint!.addressPoint.subCity.connectMyHomeActivated.toString(),
          buildingType: this.stateAddressPoint.selection,
          isElectricitySelected: this.isElectricitySelected.toString(),
          isGasSelected: this.isGasOresSelected.toString(),
          isResaSelected: this.isGasResaSelected.toString(),
          isProximusSelected: this.isProximusSelected.toString(),
          isOrangeSelected: this.isOrangeSelected.toString(),
          isSwdeSelected: this.isSwdeSelected.toString(),
          hasTooManyMetersForCmh: this.hasTooManyMetersForCmh.toString(),
          hasEnergyProduction: this.cmhEnergyProductionConfiguration.yesNoMaybeSelection?.toString() ?? '',
          flatNumber: this.stateAddressPoint.flatNumber?.toString() ?? '',
          commonElectricMeterNumber: this.stateAddressPoint.commonElectricMeterNumber?.toString() ?? '',
          hasDocumentsAttached: this.hasDocumentsAttached.toString(),
          meterFileInfoCount: this.stateFileInfo.length.toString(),
          buildingFileInfoCount: '0',
          otherFileInfoCount: '0',
          contactType: this.stateContactInfo.contactType ?? '',
          // electricityMeters: JSON.stringify(this.stateElectricityMeters),
          gasMeters: JSON.stringify(this.stateGasMeters)
        }

        this.$appInsights.trackEvent('WR_NCCMH_Step8_Validated', trackData)

        return true
      } else {
        return false
      }
    }
    return false
  }

  public openSwdeInfoModal() {
    this.isSwdeInfoModalOpen = true
  }
  //#endregion
}
</script>

<style>
div.swde-info-modal div.lib__dialogs__box {
  box-shadow: 0 8px 16px 0 rgba(12, 0, 51, 0.1);
}

div.swde-info-modal div.lib__dialogs.lib__dialogs--open {
  opacity: 0.95;
  background-color: #f9f8f8;
}

div.swde-info-modal div.lib__dialogs__box {
  width: 90%;
  height: auto;
}
</style>

<style scoped>
@media screen and (max-width: 576px) {
  .summary-conditions {
    font-size: 12px;
  }
}

img.orange-img {
  height: 30px;
}

.icon-swde-info {
  color: #0096ce;
}
</style>
